import { Text } from "@/design-system";
import { formatCurrency, formatDate } from "../utils/finance";

export type CarneSlipData = {
  number: number;
  total: number;
  dueDate: Date;
  amount: number;
};

export type CarneHeader = {
  storeName: string;
  storeContact: string;
  pixKey: string;
  customerName: string;
  customerDocument: string;
  orderReference: string;
};

type CarneSlipProps = {
  slip: CarneSlipData;
  header: CarneHeader;
};

const pad = (value: number) => String(value).padStart(2, "0");

/**
 * Uma folha do carnê: canhoto que fica na loja + via do cliente, separados pelo
 * picote. Pensado para sair bem em impressora preto e branco — hierarquia por
 * peso e borda, nada que dependa de cor de fundo.
 */
export const CarneSlip = ({ slip, header }: CarneSlipProps) => {
  const parcel = `${pad(slip.number)}/${pad(slip.total)}`;
  const dueDate = formatDate(slip.dueDate);
  const amount = formatCurrency(slip.amount);

  return (
    <li className="rounded-ds-md border-ds-ink-400 flex break-inside-avoid flex-col border sm:flex-row print:flex-row">
      <div className="border-ds-ink-400 flex flex-col gap-1 border-b border-dashed p-3 sm:w-2/5 sm:border-r sm:border-b-0 print:w-2/5 print:border-r print:border-b-0">
        <Text size="caption" tone="subtle" weight="semibold">
          Canhoto da loja
        </Text>
        <Text size="small" weight="semibold">
          {header.storeName || "Nome da loja"}
        </Text>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1">
          <Text as="dt" size="caption" tone="subtle">
            Parcela
          </Text>
          <Text as="dd" size="small" weight="semibold" numeric>
            {parcel}
          </Text>
          <Text as="dt" size="caption" tone="subtle">
            Vencimento
          </Text>
          <Text as="dd" size="small" numeric>
            {dueDate}
          </Text>
          <Text as="dt" size="caption" tone="subtle">
            Valor
          </Text>
          <Text as="dd" size="small" weight="semibold" numeric>
            {amount}
          </Text>
        </dl>
        <Text size="caption" className="break-words">
          {header.customerName || "Nome do cliente"}
        </Text>
        <Text size="caption" tone="subtle" className="mt-auto pt-2">
          Pago em ____/____/______
        </Text>
      </div>

      <div className="flex flex-1 flex-col gap-2 p-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col">
            <Text size="small" weight="semibold" className="break-words">
              {header.storeName || "Nome da loja"}
            </Text>
            {header.storeContact ? (
              <Text size="caption" tone="subtle" className="break-words">
                {header.storeContact}
              </Text>
            ) : null}
          </div>
          <Text
            size="caption"
            tone="subtle"
            weight="semibold"
            className="shrink-0"
          >
            Via do cliente
          </Text>
        </div>
        <dl className="grid grid-cols-3 gap-2">
          <div className="flex flex-col">
            <Text as="dt" size="caption" tone="subtle">
              Parcela
            </Text>
            <Text as="dd" weight="semibold" numeric>
              {parcel}
            </Text>
          </div>
          <div className="flex flex-col">
            <Text as="dt" size="caption" tone="subtle">
              Vencimento
            </Text>
            <Text as="dd" weight="semibold" numeric>
              {dueDate}
            </Text>
          </div>
          <div className="flex flex-col">
            <Text as="dt" size="caption" tone="subtle">
              Valor
            </Text>
            <Text as="dd" weight="semibold" numeric>
              {amount}
            </Text>
          </div>
        </dl>
        <Text size="small" className="break-words">
          Cliente: {header.customerName || "Nome do cliente"}
          {header.customerDocument ? ` · ${header.customerDocument}` : ""}
        </Text>
        {header.orderReference ? (
          <Text size="caption" tone="subtle" className="break-words">
            Referência: {header.orderReference}
          </Text>
        ) : null}
        {header.pixKey ? (
          <Text size="caption" className="break-words">
            Pague por Pix na chave: <strong>{header.pixKey}</strong>
          </Text>
        ) : null}
        <Text size="caption" tone="subtle" className="mt-auto pt-1">
          Recebido em ____/____/______ · Assinatura ______________________
        </Text>
      </div>
    </li>
  );
};
